import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography,
} from '@mui/material';
import { deleteDocumentoProcessoService } from '../../../../services/core/processos/processoDocumentoService';
import { extractResponseErrorMessage } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível excluir o documento.';

/**
 * Port de `handleShowDeleteDialog`/`OptionDialogNew` (tela real,
 * `DocumentosProcesso.jsx`) para MUI v5 — AW2-1094 Etapa 5. Diferente de
 * `DialogExcluirTitulo.jsx` (que exige um Motivo obrigatório), a exclusão de
 * documento real não tem esse campo — confirmação simples, mesmo texto da
 * tela real ("Deseja realmente excluir o documento?").
 *
 * Guarda de sessão/duplo-clique compartilhada — ver
 * `src/prototipo/hooks/useSessaoDialogMutacao.js`.
 */
export default function DialogExcluirDocumentoProcesso({
  open, documento, idProcesso, onClose, onSucesso,
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setErro('');
    setEnviando(false);
  });

  const onConfirmar = async () => {
    if (enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');

    try {
      await deleteDocumentoProcessoService(documento.id);
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onSucesso();
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Deseja realmente excluir o documento?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onConfirmar} variant="contained" color="error" disabled={enviando}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogExcluirDocumentoProcesso.propTypes = {
  open: PropTypes.bool,
  documento: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
};

DialogExcluirDocumentoProcesso.defaultProps = {
  open: false,
  documento: null,
  idProcesso: null,
};
